import {
  type CreateParticipantRequest,
  Gender,
  ParticipantStatus,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { dateTimeSchema, P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateParticipantMutation } from '../../graphql/hooks/useCreateParticipantMutation';
import { useParticipantFieldGroupTemplatesQuery } from '../../graphql/hooks/useParticipantFieldGroupTemplatesQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantForm } from './ParticipantForm';

type FormValues = CreateParticipantRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  organizationId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  dob: dateTimeSchema,
  gender: z.enum(Gender),
  address: z.string().nullish(),
  postcode: z.string().nullish(),
  suburb: z.string().nullish(),
  state: z.string().nullish(),
  locationId: z.string(),
  labelIds: z.array(z.string()),
  status: z.enum(ParticipantStatus),
  ndisNumber: z.string().nullish(),
  email: z.email().nullish(),
  phone: z.string().nullish(),
  picture: z.string().nullish(),
  keyContacts: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      phone: z.string().nullish(),
      email: z.email().nullish(),
      description: z.string().nullish(),
      labelIds: z.array(z.string()),
    }),
  ),
  fieldGroups: z.array(
    z.object({
      participantFieldGroupTemplateId: z.string(),
      fields: z.array(
        z.object({
          participantFieldTemplateId: z.string(),
          value: z.any(),
        }),
      ),
    }),
  ),
});

export const ParticipantCreateModal = createModal((props) => {
  const { modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { data } = useParticipantFieldGroupTemplatesQuery({
    organizationId: organization.id,
  });

  const { hide, remove, visible } = modal;

  const methods = useForm<FormValues>({
    defaultValues: {
      organizationId: organization.id,
      firstName: undefined,
      lastName: undefined,
      dob: undefined,
      gender: undefined,
      address: undefined,
      postcode: undefined,
      suburb: undefined,
      state: undefined,
      locationId: undefined,
      labelIds: [],
      status: ParticipantStatus.New,
      ndisNumber: undefined,
      email: undefined,
      phone: undefined,
      picture: undefined,
      fieldGroups: [],
      keyContacts: [],
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  React.useEffect(() => {
    const participantFieldGroupTemplates = data?.organization?.participantFieldGroupTemplates ?? [];
    const fieldGroups = participantFieldGroupTemplates.map((group) => {
      return {
        participantFieldGroupTemplateId: group.id,
        template: group,
        fields: group.fields.map((field) => {
          return {
            participantFieldTemplateId: field.id,
            template: field,
          };
        }),
      };
    });

    methods.setValue('fieldGroups', fieldGroups);
  }, [data, methods]);

  const mutation = useCreateParticipantMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const fieldGroups = values.fieldGroups.map((group) => {
        return {
          participantFieldGroupTemplateId: group.participantFieldGroupTemplateId,
          fields: group.fields.map((field) => {
            return {
              participantFieldTemplateId: field.participantFieldTemplateId,
              value: field.value,
            };
          }),
        };
      });

      await mutation
        .mutateAsync({
          request: {
            ...P.deepTrim(values),
            fieldGroups,
          },
        })
        .then(() => {
          hide();
          successToast('Participant created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating participant: ${err.message}`);
        });
    },
    [mutation, hide, errorToast, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='xl'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Add a participant'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantForm />
              </ChakraV3.Dialog.Body>

              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
